import { useAuth0 } from '@auth0/auth0-react';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { useCallback } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import * as Sentry from '@sentry/react';

export interface ApiResponse<DataType> {
  data: DataType | null;
  error: { message: string; status?: number; details?: unknown } | null;
}

export const makeApiCall = async <DataType>(config: AxiosRequestConfig): Promise<ApiResponse<DataType>> => {
  try {
    const response: AxiosResponse<DataType> = await axios(config);
    const { data } = response;

    return {
      data,
      error: null,
    };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    const { response } = error;
    const message = error.message ?? response?.statusText ?? 'http request failed';
    const status = response?.status;
    if (status && (status >= 500 || status === 401 || status === 403)) {
      Sentry.captureException(error, {
        tags: { error_category: 'api' },
        contexts: { api: { url: config.url, method: config.method, status } },
      });
    }
    return { data: null, error: { message, status, details: response?.data } };
  }
};

export const useAuthenticatedApiCall = (baseConfig: AxiosRequestConfig) => {
  const { getAccessTokenSilently } = useAuth0();
  // using query param here, as we are making a call to the API in TenantConfigProvider and
  // hence prior to the context being set (currentTenant call)
  const [accountNumber] = useQueryParam('accountNumber', StringParam);
  const makeAuthenticatedApiCall = useCallback(
    async <DataType>(specificConfig: AxiosRequestConfig) => {
      const accessToken = await getAccessTokenSilently();
      const config: AxiosRequestConfig = {
        ...baseConfig,
        ...specificConfig,
        withCredentials: specificConfig.withCredentials ?? baseConfig.withCredentials,
        headers: {
          'content-type': 'application/json',
          ...baseConfig.headers,
          ...specificConfig.headers,
          Authorization: `Bearer ${accessToken}`,
        },
      };
      if (accountNumber) {
        config.url = `${config.url}?accountNumber=${accountNumber}`;
      }
      return makeApiCall<DataType>(config);
    },
    [getAccessTokenSilently, baseConfig, accountNumber]
  );
  return { makeAuthenticatedApiCall };
};
